export type ApiKeyStatus = 'active' | 'revoked' | 'expired'

/** Scope value meaning every endpoint the key's guard protects. Mirrors the backend constant. */
export const API_KEY_SCOPE_ALL = '*'

/** One grantable scope, as `GET /integrations/apiKey/scopes` serves it. */
export interface IApiKeyScope {
  scope: string
  label: string
}

/**
 * An issued external API credential, as the admin list sees it. Note the absence of the key
 * itself: the server stores only a hash, so a key is readable exactly once, in the response to
 * the create call ({@link ICreatedApiKey}).
 */
export interface IApiKey {
  id: number
  name: string
  description: string
  /** First characters of the key, the only handle the list has on a credential it cannot show. */
  keyPrefix: string
  scopes: string[]
  /**
   * The account the key acts as, or null for a key bound to nobody. An unbound key reaches only
   * the endpoints built for integrations; a bound one reaches whatever its account can, within its
   * scopes. The list serves the id alone, so the screen resolves the name itself.
   */
  runAsUserId: number | null
  createdBy: string
  createdAt: string
  lastUsedAt: string | null
  expiresAt: string | null
  revokedAt: string | null
  revokedBy: string
  status: ApiKeyStatus
}

/** The one and only response that carries the raw key. */
export interface ICreatedApiKey {
  record: IApiKey
  key: string
}

export const STATUS_LABELS: Record<ApiKeyStatus, string> = {
  active: 'Active',
  revoked: 'Revoked',
  expired: 'Expired',
}

/** What the list shows for a key's access, given scopes are stored as raw values. */
export const describeScopes = (scopes: string[], catalogue: IApiKeyScope[]): string => {
  if (!scopes?.length) return 'None'
  if (scopes.includes(API_KEY_SCOPE_ALL)) return 'Full access'
  return scopes.map((scope) => catalogue.find((entry) => entry.scope === scope)?.label || scope).join(', ')
}

/** Tailwind classes per status. Active is the brand colour; the dead states read as muted. */
export const STATUS_CLASS: Record<ApiKeyStatus, string> = {
  active: 'bg-shade-blue/10 text-shade-blue',
  revoked: 'bg-red-50 text-red-600',
  expired: 'bg-gray-100 text-gray-500',
}
